import type { AccountCharacter } from '@sorrem/shared';

/**
 * A character serialized for comparison. `lastUpdated` is left out: every
 * server load stamps it with the load time, so it differs even when nothing
 * changed.
 */
function serializeStoredState(character: AccountCharacter): string {
    const { lastUpdated: _loadedAt, ...stored } = character;
    return JSON.stringify(stored);
}

/**
 * Swaps a character pushed by the server into the cached character list.
 * Returns null when nothing changes: the list doesn't hold that character,
 * or the cached copy already matches.
 */
export function withCharacterUpdate(
    characters: readonly AccountCharacter[],
    character: AccountCharacter,
): AccountCharacter[] | null {
    const index = characters.findIndex((entry) => entry.id === character.id);
    const cached = characters[index];
    if (cached === undefined || serializeStoredState(cached) === serializeStoredState(character)) {
        return null;
    }

    const merged = [...characters];
    merged[index] = character;
    return merged;
}

/** Drops a deleted character from the cached list, or null when the list doesn't hold it. */
export function withoutCharacter(
    characters: readonly AccountCharacter[],
    characterId: string,
): AccountCharacter[] | null {
    const remaining = characters.filter((entry) => entry.id !== characterId);
    return remaining.length === characters.length ? null : remaining;
}
